import React, { useState, Component } from 'react';
import { TrendingUp, ArrowRight, Info } from 'lucide-react';
type Row = {
  amount: number;
  d15: number;
  d30: number;
  d45: number;
  d60: number;
};
const tableData: Row[] = [{
  amount: 100000,
  d15: 9500,
  d30: 5900,
  d45: 4400,
  d60: 3800
}, {
  amount: 200000,
  d15: 19100,
  d30: 11700,
  d45: 8800,
  d60: 7500
}, {
  amount: 300000,
  d15: 28600,
  d30: 17600,
  d45: 13200,
  d60: 11300
}, {
  amount: 400000,
  d15: 38100,
  d30: 23500,
  d45: 17600,
  d60: 15000
}, {
  amount: 500000,
  d15: 47700,
  d30: 29300,
  d45: 22000,
  d60: 18800
}, {
  amount: 600000,
  d15: 57200,
  d30: 35200,
  d45: 26400,
  d60: 22600
}, {
  amount: 700000,
  d15: 66700,
  d30: 41100,
  d45: 30800,
  d60: 26300
}, {
  amount: 800000,
  d15: 76300,
  d30: 46900,
  d45: 35200,
  d60: 30100
}, {
  amount: 900000,
  d15: 85800,
  d30: 52800,
  d45: 39600,
  d60: 33800
}, {
  amount: 1000000,
  d15: 95300,
  d30: 58700,
  d45: 44000,
  d60: 37600
}];
const COBRANZA_OPTIONS = [{
  key: 'd15',
  label: 'Cobranza 15 días'
}, {
  key: 'd30',
  label: 'Cobranza 30 días'
}, {
  key: 'd45',
  label: 'Cobranza 45 días'
}, {
  key: 'd60',
  label: 'Cobranza 60 días'
}] as const;
type CobranzaKey = (typeof COBRANZA_OPTIONS)[number]['key'];
export function CommercialTable() {
  const [selectedAmount, setSelectedAmount] = useState<number>(100000);
  const [selectedCobranza, setSelectedCobranza] = useState<CobranzaKey>('d15');
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      maximumFractionDigits: 0
    }).format(value);
  };
  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('es-AR').format(value);
  };
  const selectedRow = tableData.find(r => r.amount === selectedAmount)!;
  const cobranzaLabel = COBRANZA_OPTIONS.find(o => o.key === selectedCobranza)!.label;
  const dailyValue = selectedRow[selectedCobranza];
  const handleWhatsApp = () => {
    const phone = '5491122696510';
    const message = `¡Hola Finance Group! 👋\n\nQuiero solicitar un crédito del *Sector Comercial - Diarios*:\n\n💰 *Monto:* ${formatCurrency(selectedAmount)}\n🗓️ *Plan de cobranza:* ${cobranzaLabel}\n💳 *Cobranza diaria:* ${formatCurrency(dailyValue)}\nℹ️ El sector comercial *no* cuenta con débito automático.\n\n¿Me podrían asesorar con los requisitos?`;
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };
  return <section id="comercial" className="py-20 bg-gray-50" data-id="element-71">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-72">
        <div className="text-center mb-12" data-id="element-73">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 text-blue-900 mb-6" data-id="element-74">
            <TrendingUp size={32} data-id="element-75" />
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-blue-900 mb-4" data-id="element-76">
            Sector Comercial - Diarios
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-77">
            Planilla de cobranzas diarias para comercios. Encontrá el plan que
            mejor se adapte a tu flujo de caja.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200" data-id="element-78">
          <div className="overflow-x-auto" data-id="element-79">
            <table className="w-full text-center border-collapse" data-id="element-80">
              <thead data-id="element-81">
                <tr className="bg-blue-900 text-white text-sm md:text-base" data-id="element-82">
                  <th className="py-4 px-4 font-bold border-r border-blue-800" data-id="element-83">
                    MONTO
                  </th>
                  <th className="py-4 px-4 font-bold border-r border-blue-800" data-id="element-84">
                    Cobranza 15 días
                  </th>
                  <th className="py-4 px-4 font-bold border-r border-blue-800" data-id="element-85">
                    Cobranza 30 días
                  </th>
                  <th className="py-4 px-4 font-bold border-r border-blue-800" data-id="element-86">
                    Cobranza 45 días
                  </th>
                  <th className="py-4 px-4 font-bold" data-id="element-87">Cobranza 60 días</th>
                </tr>
              </thead>
              <tbody className="text-gray-700 text-sm md:text-base" data-id="element-88">
                {tableData.map((row, index) => <tr key={row.amount} className={`${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-blue-50 transition-colors`} data-id="element-89">
                    <td className="py-3 px-4 font-bold text-blue-900 border-r border-gray-200 border-b" data-id="element-90">
                      {formatCurrency(row.amount)}
                    </td>
                    <td className="py-3 px-4 border-r border-gray-200 border-b font-medium" data-id="element-91">
                      {formatNumber(row.d15)}
                    </td>
                    <td className="py-3 px-4 border-r border-gray-200 border-b font-medium" data-id="element-92">
                      {formatNumber(row.d30)}
                    </td>
                    <td className="py-3 px-4 border-r border-gray-200 border-b font-medium" data-id="element-93">
                      {formatNumber(row.d45)}
                    </td>
                    <td className="py-3 px-4 border-b font-medium" data-id="element-94">
                      {formatNumber(row.d60)}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="bg-gray-100 p-4 text-center text-sm text-gray-500 font-medium" data-id="element-95">
            Valores expresados en pesos argentinos (ARS). Sujeto a evaluación
            crediticia.
          </div>
        </div>

        {/* Selector + Solicitud por WhatsApp */}
        <div className="mt-10 bg-white rounded-2xl shadow-xl border border-gray-200 p-6 md:p-8" data-id="element-96">
          <h3 className="text-xl font-bold text-blue-900 mb-6 text-center" data-id="element-97">
            Solicitá tu plan comercial
          </h3>

          <div className="grid sm:grid-cols-2 gap-6" data-id="element-98">
            <div data-id="element-99">
              <label htmlFor="commercialAmount" className="block text-sm font-bold text-gray-700 mb-2" data-id="element-100">
                Monto
              </label>
              <select id="commercialAmount" value={selectedAmount} onChange={e => setSelectedAmount(Number(e.target.value))} className="w-full bg-white border border-gray-300 text-gray-900 text-base rounded-lg focus:ring-blue-900 focus:border-blue-900 block p-3 font-medium" data-id="element-101">
                {tableData.map(row => <option key={row.amount} value={row.amount} data-id="element-102">
                    {formatCurrency(row.amount)}
                  </option>)}
              </select>
            </div>

            <div data-id="element-103">
              <label htmlFor="commercialCobranza" className="block text-sm font-bold text-gray-700 mb-2" data-id="element-104">
                Plan de cobranza
              </label>
              <select id="commercialCobranza" value={selectedCobranza} onChange={e => setSelectedCobranza(e.target.value as CobranzaKey)} className="w-full bg-white border border-gray-300 text-gray-900 text-base rounded-lg focus:ring-blue-900 focus:border-blue-900 block p-3 font-medium" data-id="element-105">
                {COBRANZA_OPTIONS.map(o => <option key={o.key} value={o.key} data-id="element-106">
                    {o.label}
                  </option>)}
              </select>
            </div>
          </div>

          <div className="mt-6 bg-blue-50 border border-blue-100 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" data-id="element-107">
            <span className="text-sm font-medium text-blue-900" data-id="element-108">
              Cobranza diaria estimada
            </span>
            <span className="text-2xl font-extrabold text-blue-900" data-id="element-109">
              {formatCurrency(dailyValue)}
            </span>
          </div>

          <div className="mt-4 flex items-start gap-2 text-sm text-gray-500" data-id="element-110">
            <Info className="w-4 h-4 shrink-0 mt-0.5 text-gray-400" data-id="element-111" />
            <p data-id="element-112">
              El sector comercial <span className="font-semibold" data-id="element-113">no</span>{' '}
              cuenta con la opción de débito automático.
            </p>
          </div>

          <button onClick={handleWhatsApp} className="mt-6 w-full bg-green-500 hover:bg-green-400 text-white font-bold py-4 px-6 rounded-xl flex items-center justify-center gap-2 transition-colors shadow-lg hover:shadow-xl text-lg" data-id="element-114">
            Solicitar por WhatsApp
            <ArrowRight className="w-5 h-5" data-id="element-115" />
          </button>
        </div>
      </div>
    </section>;
}